import { waitForAuthUser } from '../authedFetch';

/**
 * Membership 5787 — API calls against the backend contract:
 *
 *   GET  /api/membership/tiers              -> [{ id, name, monthlyPrice, annualPrice, isFree, notes }]
 *   GET  /api/membership/seat-lock-info     -> { roshHashanahStart, lockDate, locked }
 *   POST /api/membership/enroll             -> { clientSecret } (or the free-tier confirmation)
 *   POST /api/membership/enroll-guest-seats -> { clientSecret } for a one-time
 *        PaymentIntent at the non-member seat rate. On success the backend
 *        posts a QuickBooks SalesReceipt and sends the seats-reserved email —
 *        nothing further for the frontend to do once payment confirms.
 *
 * Tiers, seat-lock-info and guest-seat enroll are public. Membership
 * enroll stays public too (unsigned-in visitors can still join) but
 * sends the Firebase ID token when someone is already signed in so the
 * backend can attach the membership to that login.
 */

// REACT_APP_BASE_URL already ends in `/api` in every environment, so
// paths here must NOT repeat it — doing so produced /api/api/... and
// 404'd in production. Matches the convention in the other api modules.
const baseUrl = () => process.env.REACT_APP_BASE_URL || '';

const enrollHeaders = async () => {
  const headers = { 'Content-Type': 'application/json' };
  const user = await waitForAuthUser();
  if (user) {
    headers.Authorization = `Bearer ${await user.getIdToken()}`;
  }
  return headers;
};

export const getMembershipTiers = async () => {
  const res = await fetch(`${baseUrl()}/membership/tiers`);
  if (!res.ok) {
    throw new Error(`Could not load membership tiers (${res.status})`);
  }
  const data = await res.json();
  // Tolerate either a bare array or { tiers: [...] } from the backend.
  return Array.isArray(data) ? data : data.tiers || [];
};

export const getSeatLockInfo = async () => {
  const res = await fetch(`${baseUrl()}/membership/seat-lock-info`);
  if (!res.ok) {
    throw new Error(`Could not load seat availability (${res.status})`);
  }
  return res.json();
};

// payload: { tierId, billingCycle, household, hhSeats } per buildEnrollPayload()
export const enrollInMembership = async (payload) => {
  const res = await fetch(`${baseUrl()}/membership/enroll`, {
    method: 'POST',
    headers: await enrollHeaders(),
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.msg || data.message || `Could not submit enrollment (${res.status})`);
  }
  return data;
};

// payload: { contact: { name, email }, hhSeats: { menSeatNames, womenSeatNames } }
// per buildGuestSeatsPayload(). `hhSeats` is the key the backend validates
// (kbt-backend membership/guestSeatValidation.js) — not `seats`.
export const enrollGuestSeats = async (payload) => {
  const res = await fetch(`${baseUrl()}/membership/enroll-guest-seats`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.msg || data.message || `Could not submit guest seat request (${res.status})`);
  }
  return data;
};

// GET /api/membership/receipt?paymentIntent=pi_xxx -> { ready, amount?, receiptPdfUrl?, receiptHostedUrl? }
// Used by the thank-you page after Stripe redirects back from checkout.
// The receipt PDF is generated asynchronously by the backend's Stripe
// webhook, so `ready: false` is a normal "still processing" response, not
// an error — the caller is expected to poll a few times.
export const getMembershipReceipt = async (paymentIntentId) => {
  const res = await fetch(`${baseUrl()}/membership/receipt?paymentIntent=${encodeURIComponent(paymentIntentId)}`);
  if (!res.ok) {
    throw new Error(`Could not check receipt status (${res.status})`);
  }
  return res.json();
};
